import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useDivisionFromQuery } from "../hooks/useDivisionFromQuery";

interface FAQItemProps {
  question: string;
  answer: string | React.ReactNode;
}

function FAQItem({ question, answer }: FAQItemProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="faq-item">
      <h4
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {question}
        <span>{isOpen ? "−" : "+"}</span>
      </h4>
      {isOpen && <p>{answer}</p>}
    </div>
  );
}

export default function FAQPage() {
  useDivisionFromQuery();

  return (
    <div className="app-container">
      <Header />

      <main className="main-content content-page">
        <h2>Frequently Asked Questions</h2>

        <div className="faq-section">
          <h3>Handling Common Questions</h3>

          <FAQItem
            question='"How is this different from other energy shots?"'
            answer="Proper Wild uses organic, plant-based caffeine from green tea instead of synthetic caffeine. It also contains L-theanine for smooth, focused energy with nothing artificial. Proper Wild uses the scientifically backed combination of 100mg of caffeine (about 1 cup of coffee) plus 120mg of L-Theanine (15x more than a cup of green tea) to provide a longer lasting energy boost with reduced jitters and crash."
          />

          <FAQItem
            question='"Will this make me super jittery?"'
            answer="No! The L-theanine in Proper Wild works with the caffeine to provide calm, focused energy while reducing the jitters and crash that most people get from coffee or energy drinks."
          />

          <FAQItem
            question='"Is this safe to drink daily?"'
            answer="Yes! Proper Wild is made with plant-based ingredients and contains about the same caffeine as 1 cup of coffee (100mg). As with any caffeinated product, just stay within recommended daily caffeine limits."
          />

          <FAQItem
            question='"How and when will I be reimbursed?"'
            answer="Customers will be reimbursed by PayPal or Venmo within 3 daysfor the first shot they redeem (excludes sales tax). Plus they're automatically entered for a chance to win $20,000!"
          />

          <FAQItem
            question='"Can customers submit multiple receipts?"'
            answer="Yes, every shot they redeem automatically enters them for a chance to win $20,000! Every 5 shots are free (after the first one). Customers can submit multiple shots on a single receipt or submit multiple receipts."
          />
        </div>

        <div className="faq-section">
          <h3>Support</h3>

          <FAQItem
            question="Where can customers learn more?"
            answer={
              <>
                Have them visit{" "}
                <a
                  href="https://mandysmillion.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  MandysMillion.com
                </a>{" "}
                to learn more about the competition and how to participate. Or
                visit{" "}
                <a
                  href="https://properwild.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ProperWild.com
                </a>{" "}
                to learn more about the product.
              </>
            }
          />

          <FAQItem
            question="Who can I contact for help?"
            answer="For technical support, competition questions, or general inquiries, please contact the Proper Wild team by emailing support@properwild.com"
          />

          <FAQItem
            question="Who should customers contact about redemption issues?"
            answer="They should text SUPPORT to the number they used to sign up for the program."
          />
        </div>

        <div className="faq-section">
          <h3>About the Competition</h3>

          <FAQItem
            question="What is the Proper Wild Clerk Competition?"
            answer="A two‑month, nationwide performance competition for in‑store clerks. No judging and no random draws. Winners are based solely on the number of verified customer redemptions of Proper Wild shots at your store (tracked by our partner Aisle). Prizes are paid to the store, and stores decide how to distribute to clerks."
          />

          <FAQItem
            question="How are stores divided into divisions?"
            answer="Stores are divided into five divisions based on their chain size. This ensures fair competition among stores of similar capacity."
          />

          <FAQItem
            question="How often is the leaderboard updated?"
            answer="The leaderboard is updated daily with the latest redemption data from the Aisle platform. You can see the last update time at the bottom of each leaderboard page."
          />
        </div>

        <div className="faq-section">
          <h3>Rankings and Scoring</h3>

          <FAQItem
            question="How are stores ranked?"
            answer="Stores are ranked by total redemptions within their division. In case of a tie, the store with the earliest redemption timestamp receives the higher rank."
          />

          <FAQItem
            question="What counts as a redemption?"
            answer="A redemption occurs when a customer buys a Proper Wild product in store and submits a photo of the receipt to the text number they used to sign up for the program. Only redemptions within the active competition window count toward the leaderboard."
          />
        </div>

        <div className="faq-section">
          <h3>Technical Questions</h3>

          <FAQItem
            question="Why don't I see my store on the leaderboard?"
            answer="If your store is not appearing, it may be because: (1) no redemptions have been recorded yet, (2) your store hasn't been matched to a division, or (3) there's a data sync delay. Contact support@properwild.com if your store should be listed."
          />

          <FAQItem
            question="How do I report an error with the leaderboard data?"
            answer="If you notice incorrect data or ranking issues, please contact Proper Wild support (support@properwild.com) with your store name, division, and a description of the issue."
          />
        </div>
      </main>

      <Footer />
    </div>
  );
}
